import type { ReactNode } from "react";
import { createElement } from "react";

/**
 * App-wide constants.
 *
 * `DEFAULT_LOCATION_SLUG` is the fallback for components that need
 * a location before the user has chosen one (top bar, sign-in page,
 * new plan form). When multi-city launches, this can become a config
 * lookup or user-preference read.
 *
 * SEO Build Tasks §2.6 / §6.6.
 */

export const DEFAULT_LOCATION_SLUG = "bend-or";

/**
 * Site-wide config object for location/season text.
 *
 * Every surface that displays location or season text reads from
 * this object — never hardcoded "Bend" or "Summer 2026" strings.
 *
 * When multi-city launches, this becomes a function that reads from
 * the `locations` table based on the current route context. For now,
 * single-city Bend values.
 *
 * V2 Platform Feature Spec §1.4.
 */
/** Current planning year — after August, rolls forward to next summer. */
const _now = new Date();
const SUMMER_YEAR = _now.getMonth() >= 8 ? _now.getFullYear() + 1 : _now.getFullYear();

export const SITE = {
  season: "Summer",
  year: String(SUMMER_YEAR),
  yearNum: SUMMER_YEAR,
  seasonYear: `Summer ${SUMMER_YEAR}`,
  location: "Bend",
  region: "Bend, Oregon",
  activityLabel: "camp",
  activityLabelPlural: "camps",
  activityLabelCap: "Camp",
  activityLabelPluralCap: "Camps",
  /** Premium pricing — displayed on paywall and hero */
  premiumPriceDisplay: "$89/year",
  premiumPriceCents: 8900,
} as const;

/**
 * Free accounts can save up to this many camps before the paywall fires.
 * Drives the account-menu "X of N used" Saved hint (#1011) and the
 * save-limit paywall copy.
 */
export const FREE_SAVED_LIMIT = 5;

/**
 * Kid color palette — assigned in order on kid creation.
 * 6 colors that are visually distinct on both light and dark backgrounds.
 * Must pass WCAG AA contrast on white text when used as solid fills.
 */
export const KID_COLORS = [
  "#1a5e5e", // teal
  "#6B4E6B", // plum
  "#6B7A5A", // sage
  "#4E6B7A", // slate
  "#8B6F5E", // leather (legacy — kept so existing kids round-trip)
] as const;

/**
 * Canonical category list — unified taxonomy (M82 categories table).
 * Single source of truth for hero filter chips and finder filter panel.
 * Slugs match categories.slug values in the database (new taxonomy).
 *
 * CATEGORY_SLUG_MAP maps legacy camp_categories slugs to new taxonomy slugs
 * for backward compatibility throughout the codebase.
 */
export const CAMP_CATEGORIES = [
  { slug: "outdoors-nature", label: "Outdoors & Nature", dotColor: "#1A5E5E", svgIcon: "/icons/outdoor.svg" },
  { slug: "sports", label: "Sports", dotColor: "#4E6B7A", svgIcon: "/icons/sports.svg" },
  { slug: "arts-crafts", label: "Arts & Crafts", dotColor: "#6B4E6B", svgIcon: "/icons/art.svg" },
  { slug: "performing-arts", label: "Performing Arts", dotColor: "#6B4E6B", svgIcon: "/icons/performing-arts.svg" },
  { slug: "enrichment", label: "Enrichment", dotColor: "#C26B4A", svgIcon: "/icons/enrichment.svg" },
  { slug: "general", label: "General / Multi-Activity", dotColor: "#3A6E3A", svgIcon: "/icons/multi-activity.svg" },
  { slug: "stem", label: "STEM", dotColor: "#D4953A", svgIcon: "/icons/stem.svg" },
  { slug: "special-needs", label: "Special Needs & Adaptive", dotColor: "#C26B4A", svgIcon: "/icons/adaptive.svg" },
  // #1324: no dedicated brand art exists for Academic or Medical & Health, and
  // commissioning new icons is a Lisa/brand decision. Until then these reuse the
  // closest existing canonical glyph intentionally (not a placeholder): Academic
  // shares STEM's learning/knowledge glyph (m290 buckets it alongside STEM), and
  // Medical & Health reuses the Enrichment glyph (NOT adaptive.svg — that is the
  // dedicated special-needs glyph above and connotes disability accommodation).
  // The camp-category-icon-exists contract test pins that both resolve to real files.
  { slug: "academic", label: "Academic", dotColor: "#D4953A", svgIcon: "/icons/stem.svg" },
  { slug: "medical-health", label: "Medical & Health", dotColor: "#4E6B7A", svgIcon: "/icons/enrichment.svg" },
] as const satisfies ReadonlyArray<{ slug: string; label: string; dotColor: string; svgIcon: string }>;

export const PLACE_CATEGORIES = [
  { slug: "retail", label: "Retail", dotColor: "#8B6F5E", svgIcon: "/icons/multi-activity.svg" },
  { slug: "dining", label: "Dining", dotColor: "#C26B4A", svgIcon: "/icons/multi-activity.svg" },
  { slug: "places", label: "Places", dotColor: "#1A5E5E", svgIcon: "/icons/outdoor.svg" },
  { slug: "events", label: "Events", dotColor: "#3A6E3A", svgIcon: "/icons/multi-activity.svg" },
  { slug: "entertainment", label: "Entertainment", dotColor: "#6B4E6B", svgIcon: "/icons/multi-activity.svg" },
  { slug: "childcare", label: "Childcare", dotColor: "#4E6B7A", svgIcon: "/icons/enrichment.svg" },
] as const;

/**
 * Maps old camp_categories slugs to new unified taxonomy slugs.
 * Use this when converting legacy slug references throughout the codebase.
 */
export const CATEGORY_SLUG_MAP: Record<string, string> = {
  outdoor: "outdoors-nature",
  sport: "sports",
  art: "arts-crafts",
  music: "performing-arts",
  enrichment: "enrichment",
  "multi-activity": "general",
  stem: "stem",
  adaptive: "special-needs",
} as const;

/**
 * Subcategory taxonomy — V3 mockup.
 * Maps each CAMP_CATEGORIES slug to its drill-down subcategories.
 * Used for kid interest selection and camp filtering.
 */
export const CAT_SUBS: Record<string, Array<{ label: string }>> = {
  "outdoors-nature": [
    { label: "Hiking" },
    { label: "Fishing" },
    { label: "Wildlife/Ecology" },
    { label: "Gardening" },
    { label: "Wilderness Skills" },
    { label: "Horseback Riding" },
    { label: "Paddling" },
    { label: "Mountain Biking" },
    { label: "Camping/Backpacking" },
  ],
  sports: [
    { label: "Soccer" },
    { label: "Basketball" },
    { label: "Baseball/Softball" },
    { label: "Swimming" },
    { label: "Tennis" },
    { label: "Gymnastics" },
    { label: "Martial Arts" },
    { label: "Multi-Sport" },
    { label: "Climbing" },
    { label: "Cycling" },
  ],
  "arts-crafts": [
    { label: "Visual Arts" },
    { label: "Ceramics/Pottery" },
    { label: "Mixed Media" },
    { label: "Fiber Arts" },
    { label: "Digital Art" },
    { label: "Film/Video" },
    { label: "Photography" },
    { label: "Woodworking" },
  ],
  "performing-arts": [
    { label: "Theater/Drama" },
    { label: "Dance" },
    { label: "Music/Band" },
    { label: "Circus Arts" },
    { label: "Improv/Comedy" },
  ],
  enrichment: [
    { label: "Cooking/Baking" },
    { label: "Language" },
    { label: "Leadership" },
    { label: "Life Skills" },
    { label: "Chess" },
    { label: "Debate" },
  ],
  general: [
    { label: "Traditional Day Camp" },
    { label: "Overnight Camp" },
    { label: "Before/After Care" },
    { label: "Drop-In" },
  ],
  stem: [
    { label: "Coding/Programming" },
    { label: "Robotics" },
    { label: "Engineering/Building" },
    { label: "Science Lab" },
    { label: "Math" },
    { label: "Space/Astronomy" },
    { label: "Maker/Fabrication" },
  ],
  "special-needs": [
    { label: "Adaptive Sports" },
    { label: "Sensory-Friendly" },
    { label: "Therapeutic Recreation" },
    { label: "Social Skills" },
  ],
  academic: [
    { label: "Reading/Writing" },
    { label: "Test Prep" },
    { label: "Tutoring" },
    { label: "Study Skills" },
    { label: "College Prep" },
  ],
  "medical-health": [
    { label: "Pediatrics" },
    { label: "Therapy (OT/PT/Speech)" },
    { label: "Mental Health/Counseling" },
  ],
  retail: [
    { label: "Clothing" },
    { label: "Toys & Games" },
    { label: "Books" },
    { label: "Sporting Goods" },
  ],
  dining: [
    { label: "Pizza" },
    { label: "Ice Cream/Treats" },
    { label: "Bakery/Cafe" },
    { label: "Sit-Down Family" },
  ],
  places: [
    { label: "Trails" },
    { label: "Parks" },
    { label: "Playgrounds" },
    { label: "Lakes/Rivers" },
  ],
  events: [
    { label: "Festivals" },
    { label: "Farmers Markets" },
    { label: "Holiday Events" },
    { label: "Fun Runs/Races" },
  ],
  entertainment: [
    { label: "Trampoline/Bounce" },
    { label: "Bowling" },
    { label: "Arcade/Gaming" },
    { label: "Indoor Playground" },
    { label: "Museum" },
  ],
  childcare: [
    { label: "Daycare Center" },
    { label: "Preschool/Pre-K" },
    { label: "Montessori" },
    { label: "Forest School" },
  ],

  // ── Legacy keys (backward compat — alias to new taxonomy entries) ──
  outdoor: [
    { label: "Hiking" },
    { label: "Fishing" },
    { label: "Wildlife/Ecology" },
    { label: "Wilderness Skills" },
    { label: "Paddling" },
    { label: "Mountain Biking" },
    { label: "Camping/Backpacking" },
  ],
  sport: [
    { label: "Soccer" },
    { label: "Basketball" },
    { label: "Baseball/Softball" },
    { label: "Swimming" },
    { label: "Tennis" },
    { label: "Martial Arts" },
    { label: "Gymnastics" },
    { label: "Multi-Sport" },
    { label: "Climbing" },
  ],
  art: [
    { label: "Visual Arts" },
    { label: "Ceramics/Pottery" },
    { label: "Mixed Media" },
    { label: "Fiber Arts" },
    { label: "Digital Art" },
  ],
  music: [
    { label: "Theater/Drama" },
    { label: "Dance" },
    { label: "Music/Band" },
    { label: "Circus Arts" },
    { label: "Improv/Comedy" },
  ],
  "multi-activity": [
    { label: "Traditional Day Camp" },
    { label: "Overnight Camp" },
    { label: "Before/After Care" },
    { label: "Drop-In" },
  ],
  adaptive: [
    { label: "Adaptive Sports" },
    { label: "Sensory-Friendly" },
    { label: "Therapeutic Recreation" },
    { label: "Social Skills" },
  ],
};

/**
 * Category gradient map for CampPhoto fallback cards.
 * Keys match camp_categories.slug values.
 */
export const CATEGORY_GRADIENTS: Record<
  string,
  { from: string; to: string; icon: string }
> = {
  // ── New taxonomy slugs ─────────────────────────────────────
  "outdoors-nature": { from: "#1a5e5e", to: "#4a8c8c", icon: "🌲" },
  sports: { from: "#d4953a", to: "#c08530", icon: "⚽" },
  "arts-crafts": { from: "#c26b4a", to: "#d4953a", icon: "🎨" },
  "performing-arts": { from: "#8b5e8b", to: "#a878a8", icon: "🎵" },
  enrichment: { from: "#8b6f5e", to: "#a08878", icon: "📚" },
  general: { from: "#3a6e3a", to: "#5e8b5e", icon: "🏕️" },
  stem: { from: "#3a5ed4", to: "#5e8bd4", icon: "🔬" },
  "special-needs": { from: "#5a6e7f", to: "#7a8e9f", icon: "♿" },
  academic: { from: "#3a5ed4", to: "#5e8bd4", icon: "📖" },
  "medical-health": { from: "#5a6e7f", to: "#7a8e9f", icon: "🏥" },
  retail: { from: "#8b6f5e", to: "#a08878", icon: "🛍️" },
  dining: { from: "#c26b4a", to: "#d4953a", icon: "🍕" },
  places: { from: "#1a5e5e", to: "#4a8c8c", icon: "📍" },
  events: { from: "#3a6e3a", to: "#5e8b5e", icon: "🎪" },
  entertainment: { from: "#8b5e8b", to: "#a878a8", icon: "🎮" },
  childcare: { from: "#5a6e7f", to: "#7a8e9f", icon: "👶" },

  // ── Legacy slugs (backward compat) ─────────────────────────
  outdoor: { from: "#1a5e5e", to: "#4a8c8c", icon: "🌲" },
  nature: { from: "#1a5e5e", to: "#4a8c8c", icon: "🌲" },
  sport: { from: "#d4953a", to: "#c08530", icon: "⚽" },
  art: { from: "#c26b4a", to: "#d4953a", icon: "🎨" },
  mixed: { from: "#3a6e3a", to: "#5e8b5e", icon: "🏕️" },
  music: { from: "#8b5e8b", to: "#a878a8", icon: "🎵" },
  dance: { from: "#8b5e8b", to: "#a878a8", icon: "💃" },
  "team-building": { from: "#d4953a", to: "#c08530", icon: "🤝" },
  gaming: { from: "#3a5ed4", to: "#5e8bd4", icon: "🎮" },
  reading: { from: "#8b6f5e", to: "#a08878", icon: "📖" },
  tech: { from: "#3a5ed4", to: "#5e8bd4", icon: "💻" },
  adaptive: { from: "#5a6e7f", to: "#7a8e9f", icon: "♿" },
  "multi-activity": { from: "#3a6e3a", to: "#5e8b5e", icon: "🏕️" },
};

/**
 * Inline SVG icons for blocked-time types (C11 — cross-platform consistency).
 *
 * Each value is a ReactNode so it can be dropped straight into JSX.
 * Uses `createElement` instead of JSX so this file stays a plain `.ts` module.
 */
const svg = (d: string, size = 16) =>
  createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: size,
      height: size,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round" as const,
      strokeLinejoin: "round" as const,
      "aria-hidden": true,
    },
    createElement("path", { d }),
  );

export const BLOCK_TYPE_ICONS: Record<string, ReactNode> = {
  /** Airplane — vacation */
  vacation: svg(
    "M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z",
  ),
  /** House — family visiting */
  family: svg("M3 9.5L12 3l9 6.5V20a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z M9 21V12h6v9"),
  /** Calendar — custody week */
  custody: svg(
    "M4 5a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v15a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z M16 2v4M8 2v4M4 9h16",
  ),
  /** Circle-slash / ban — other */
  other: createElement(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: 16,
      height: 16,
      viewBox: "0 0 24 24",
      fill: "none",
      stroke: "currentColor",
      strokeWidth: 2,
      strokeLinecap: "round" as const,
      strokeLinejoin: "round" as const,
      "aria-hidden": true,
    },
    createElement("circle", { cx: 12, cy: 12, r: 10 }),
    createElement("path", { d: "M4.93 4.93l14.14 14.14" }),
  ),
};
